import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '../../contexts/AuthContext';
import {
  MessageSquare, Search, RefreshCw, Mail, FileText,
  Building2, Clock, CheckCircle, AlertCircle, Inbox,
  ChevronLeft, ChevronRight, ExternalLink, X
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOCK_INQUIRIES = [
  { id: 'inq_001', target_slug: 'istanbul-fair-solutions', target_type: 'contractor', inquiry_type: 'brief', status: 'new', created_at: '2025-02-10T09:14:00Z', sender: { name: 'Marco Russo', company: 'TechFlow Italia', email: 'marco@techflow.it', phone: '+39 02 1234567' }, brief: { show_name: 'Hannover Messe 2025', city: 'Hannover, Germany', event_dates: 'Apr 1-5, 2025', budget: '$18,000 – $22,000', stand_sqm: '48 sqm (6×8)', notes: 'Looking for a custom double-decker stand with private meeting room.' } },
  { id: 'inq_002', target_slug: 'istanbul-fair-solutions', target_type: 'contractor', inquiry_type: 'contact', status: 'in_progress', created_at: '2025-02-09T14:30:00Z', sender: { name: 'Sarah Chen', company: 'MedTech Global HK', email: 'sarah@medtech.hk', phone: '+852 2345 6789' }, contact: { subject: 'Portfolio request for Arab Health', message: 'Hi, we are considering attending Arab Health 2025 and would love to see your portfolio of medical/healthcare stands.' } },
  { id: 'inq_003', target_slug: 'modular-display-systems', target_type: 'supplier', inquiry_type: 'brief', status: 'new', created_at: '2025-02-09T11:20:00Z', sender: { name: 'Klaus Weber', company: 'AutoTech GmbH', email: 'k.weber@autotech.de' }, brief: { show_name: 'Automechanika Frankfurt 2025', city: 'Frankfurt, Germany', budget: '€8,000 – €12,000', stand_sqm: '36 sqm', notes: 'Need full modular system including flooring, lighting and graphics.' } },
  { id: 'inq_004', target_slug: 'modular-display-systems', target_type: 'supplier', inquiry_type: 'brief', status: 'resolved', created_at: '2025-02-08T08:00:00Z', sender: { name: 'Priya Nair', company: 'Infosys Dubai', email: 'p.nair@infosys.ae' }, brief: { show_name: 'GITEX Global 2025', city: 'Dubai, UAE', budget: '€15,000+', stand_sqm: '72 sqm' } },
  { id: 'inq_005', target_slug: 'istanbul-fair-solutions', target_type: 'contractor', inquiry_type: 'brief', status: 'new', created_at: '2025-02-08T16:45:00Z', sender: { name: 'Emre Yıldız', company: 'Modular Living TR', email: 'emre@modularliving.com.tr' }, brief: { show_name: 'EuroCucina 2025', city: 'Milan, Italy', event_dates: 'Apr 8-13, 2025', budget: '€20,000 – €30,000', stand_sqm: '96 sqm', notes: 'Premium kitchen brand, need immersive lifestyle showcase.' } },
  { id: 'inq_006', target_slug: 'istanbul-fair-solutions', target_type: 'contractor', inquiry_type: 'contact', status: 'in_progress', created_at: '2025-02-07T10:10:00Z', sender: { name: 'Anna Schmidt', company: 'BioTech Europa', email: 'a.schmidt@biotech.eu' }, contact: { subject: 'Urgent: Last-minute stand build', message: 'We had a contractor drop out and have Medica in 3 weeks. Can you help?' } },
];

const TYPE_CONFIG = {
  brief:   { color: 'bg-blue-100 text-blue-700',   label: 'Brief',   icon: FileText },
  contact: { color: 'bg-purple-100 text-purple-700', label: 'Contact', icon: Mail },
};
const STATUS_CONFIG = {
  new:         { color: 'bg-amber-100 text-amber-700',   label: 'Yeni' },
  in_progress: { color: 'bg-blue-100 text-blue-700',     label: 'İşlemde' },
  resolved:    { color: 'bg-emerald-100 text-emerald-700', label: 'Çözüldü' },
};
const TARGET_CONFIG = {
  contractor: { color: 'bg-teal-100 text-teal-700',   label: 'Yüklenici' },
  supplier:   { color: 'bg-indigo-100 text-indigo-700', label: 'Tedarikçi' },
};

const PAGE_SIZE = 10;

const UltraInquiries = () => {
  const { token } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [targetFilter, setTargetFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [useMock, setUseMock] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE };
      if (typeFilter   !== 'all') params.inquiry_type = typeFilter;
      if (targetFilter !== 'all') params.target_type  = targetFilter;
      if (statusFilter !== 'all') params.status       = statusFilter;
      const res = await axios.get(`${API}/ultra/inquiries`, {
        headers: { Authorization: `Bearer ${token}` }, params,
      });
      setInquiries(res.data.inquiries || []);
      setTotal(res.data.total || 0);
      setUseMock(false);
    } catch {
      setInquiries(MOCK_INQUIRIES);
      setTotal(MOCK_INQUIRIES.length);
      setUseMock(true);
    } finally { setLoading(false); }
  }, [token, page, typeFilter, targetFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/ultra/inquiries/${id}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInquiries(prev => prev.map(i => i.id === id ? { ...i, status } : i));
      if (selected?.id === id) setSelected(prev => ({ ...prev, status }));
    } catch { /* keep UI optimistic */ }
  };

  const filtered = search
    ? inquiries.filter(i =>
        i.sender?.name?.toLowerCase().includes(search.toLowerCase()) ||
        i.sender?.company?.toLowerCase().includes(search.toLowerCase()) ||
        i.target_slug?.toLowerCase().includes(search.toLowerCase()) ||
        i.brief?.show_name?.toLowerCase().includes(search.toLowerCase())
      )
    : inquiries;

  const totalPages = useMock ? Math.ceil(filtered.length / PAGE_SIZE) : Math.ceil(total / PAGE_SIZE);
  const displayed  = useMock ? filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : filtered;

  // KPI counts (from mock or total)
  const countNew     = (useMock ? MOCK_INQUIRIES : inquiries).filter(i => i.status === 'new').length;
  const countBriefs  = (useMock ? MOCK_INQUIRIES : inquiries).filter(i => i.inquiry_type === 'brief').length;
  const countContact = (useMock ? MOCK_INQUIRIES : inquiries).filter(i => i.inquiry_type === 'contact').length;

  const KPIs = [
    { label: 'Toplam Sorgu', value: useMock ? MOCK_INQUIRIES.length : total, icon: Inbox, color: 'bg-blue-50 text-blue-600' },
    { label: 'Yeni / Yanıt Bekleyen', value: countNew, icon: AlertCircle, color: 'bg-amber-50 text-amber-600' },
    { label: 'Brief Talepleri', value: countBriefs, icon: FileText, color: 'bg-teal-50 text-teal-600' },
    { label: 'İletişim Mesajları', value: countContact, icon: Mail, color: 'bg-purple-50 text-purple-600' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gelen Sorgular</h1>
          <p className="text-sm text-gray-500 mt-0.5">Genel profil sayfalarından gelen brief ve iletişim talepleri</p>
        </div>
        <div className="flex items-center gap-2">
          {useMock && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">MOCK</span>}
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" />Yenile
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {KPIs.map((k, i) => {
          const Icon = k.icon;
          return (
            <Card key={i} className="border-0 shadow-sm">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${k.color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-black text-gray-900">{k.value}</p>
                  <p className="text-xs text-gray-500">{k.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input placeholder="İsim, şirket, fuar..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9 bg-gray-50 border-gray-200" />
          </div>
          {[
            { label: 'Tür', value: typeFilter, setter: setTypeFilter, options: [['all','Tümü'],['brief','Brief'],['contact','İletişim']] },
            { label: 'Hedef', value: targetFilter, setter: setTargetFilter, options: [['all','Tümü'],['contractor','Yüklenici'],['supplier','Tedarikçi']] },
            { label: 'Durum', value: statusFilter, setter: setStatusFilter, options: [['all','Tümü'],['new','Yeni'],['in_progress','İşlemde'],['resolved','Çözüldü']] },
          ].map(f => (
            <select key={f.label} value={f.value} onChange={e => { f.setter(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
              {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          ))}
          <span className="text-sm text-gray-400 ml-auto">{useMock ? filtered.length : total} sorgu</span>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {['Gönderen', 'Hedef Profil', 'Tür', 'Detay', 'Tarih', 'Durum', 'İşlem'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}><td colSpan={7} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td></tr>
                ))
              ) : displayed.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                  <Inbox className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p>Henüz sorgu yok</p>
                </td></tr>
              ) : displayed.map(inq => {
                const tc = TYPE_CONFIG[inq.inquiry_type] || TYPE_CONFIG.contact;
                const sc = STATUS_CONFIG[inq.status] || STATUS_CONFIG.new;
                const gc = TARGET_CONFIG[inq.target_type] || TARGET_CONFIG.contractor;
                const TypeIcon = tc.icon;
                return (
                  <tr key={inq.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelected(inq)}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-900">{inq.sender?.name}</p>
                      <p className="text-xs text-gray-400">{inq.sender?.company}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${gc.color}`}>{gc.label}</span>
                        <span className="text-xs text-gray-500 font-mono">{inq.target_slug}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-semibold ${tc.color}`}>
                        <TypeIcon className="w-3 h-3" />{tc.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-[200px]">
                      <p className="text-xs text-gray-600 truncate">
                        {inq.brief?.show_name || inq.contact?.subject || '—'}
                      </p>
                      {inq.brief?.budget && <p className="text-xs text-gray-400">{inq.brief.budget}</p>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {new Date(inq.created_at).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${sc.color}`}>{sc.label}</span>
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1">
                        {inq.status === 'new' && (
                          <button onClick={() => updateStatus(inq.id, 'in_progress')}
                            className="text-xs px-2 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg font-medium">
                            İşleme Al
                          </button>
                        )}
                        {inq.status === 'in_progress' && (
                          <button onClick={() => updateStatus(inq.id, 'resolved')}
                            className="text-xs px-2 py-1 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-lg font-medium">
                            Çözüldü
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Sayfa {page} / {totalPages}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="h-7 px-2" disabled={page === 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            {[...Array(Math.min(totalPages, 5))].map((_, i) => {
              const pg = i + 1;
              return <Button key={pg} variant={page === pg ? 'default' : 'outline'} size="sm" className="h-7 w-7 p-0 text-xs" onClick={() => setPage(pg)}>{pg}</Button>;
            })}
            <Button variant="outline" size="sm" className="h-7 px-2" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-end p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mt-16 mr-0 overflow-y-auto max-h-[80vh]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Sorgu Detayı</h3>
              <button onClick={() => setSelected(null)} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Status row */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${TYPE_CONFIG[selected.inquiry_type]?.color}`}>{TYPE_CONFIG[selected.inquiry_type]?.label}</span>
                <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${STATUS_CONFIG[selected.status]?.color}`}>{STATUS_CONFIG[selected.status]?.label}</span>
                <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${TARGET_CONFIG[selected.target_type]?.color}`}>{TARGET_CONFIG[selected.target_type]?.label}: {selected.target_slug}</span>
              </div>

              {/* Sender */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Gönderen</p>
                <p className="font-bold text-gray-900">{selected.sender?.name}</p>
                <p className="text-sm text-gray-600">{selected.sender?.company}</p>
                <a href={`mailto:${selected.sender?.email}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1">
                  <Mail className="w-3.5 h-3.5" />{selected.sender?.email}
                </a>
                {selected.sender?.phone && <p className="text-sm text-gray-600 mt-0.5">{selected.sender.phone}</p>}
              </div>

              {/* Brief or Contact details */}
              {selected.brief && (
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-blue-600 mb-2 uppercase tracking-wider">Brief Bilgileri</p>
                  {[
                    ['Fuar', selected.brief.show_name],
                    ['Şehir', selected.brief.city],
                    ['Tarihler', selected.brief.event_dates],
                    ['Bütçe', selected.brief.budget],
                    ['Stand Alanı', selected.brief.stand_sqm],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="flex justify-between py-1.5 border-b border-blue-100 last:border-0">
                      <span className="text-xs text-blue-600">{k}</span>
                      <span className="text-xs text-gray-900 font-medium text-right max-w-[55%]">{v}</span>
                    </div>
                  ))}
                  {selected.brief.notes && (
                    <div className="mt-2 pt-2 border-t border-blue-100">
                      <p className="text-xs text-blue-600 mb-1">Notlar</p>
                      <p className="text-xs text-gray-700 leading-relaxed">{selected.brief.notes}</p>
                    </div>
                  )}
                </div>
              )}

              {selected.contact && (
                <div className="bg-purple-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-purple-600 mb-2 uppercase tracking-wider">Mesaj</p>
                  <p className="text-sm font-semibold text-gray-900 mb-2">{selected.contact.subject}</p>
                  <p className="text-sm text-gray-600 leading-relaxed">{selected.contact.message}</p>
                </div>
              )}

              {/* Created at */}
              <p className="text-xs text-gray-400 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {new Date(selected.created_at).toLocaleString('tr-TR')}
              </p>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <a href={`mailto:${selected.sender?.email}`}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-[#0B1E4D] text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-[#163A7D] transition-colors">
                  <Mail className="w-4 h-4" />E-posta Gönder
                </a>
                <a href={`/${selected.target_type}/${selected.target_slug}`} target="_blank" rel="noreferrer"
                  className="flex items-center justify-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold px-3 py-2.5 rounded-xl transition-colors">
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UltraInquiries;
